/**
 * ============================================================================
 * CLOSURE REMARKS WELDING REPAIR COMPONENT
 * ============================================================================
 * 
 * PURPOSE:
 * Displays and manages the approval workflow for welding repair analysis.
 * Shows revision history in accordion format with multi-level approval tracking.
 * 
 * WORKFLOW LEVELS:
 * 1. Supervisor: Initiates the repair analysis
 * 2. Welding Engineer (WE): First level approval
 * 3. QC Engineer (QCE): Second level approval  
 * 4. NDE: Final approval
 * 
 * KEY FEATURES:
 * - Accordion-based revision history display
 * - Dynamic approval form based on user role
 * - Real-time status tracking with color-coded badges
 * - Conditional field visibility based on approval status
 * - Interactive approval/rejection with comments
 * 
 * APPROVAL LOGIC:
 * - Shows approvers up to current pending level
 * - Hides future approvers until workflow reaches them
 * - Displays "Pending With" for users not yet involved
 * - Allows current approver to add comments and approve/reject
 * 
 * LOOP COUNT MECHANISM:
 * Critical variable that controls progressive disclosure of approval levels:
 * - loopCount = 1: Show only Supervisor (initial state)
 * - loopCount = 2: Show Supervisor + WE (when pending with WE)
 * - loopCount = 3: Show Supervisor + WE + QCE (when pending with QCE)
 * - loopCount = 4: Show all levels (when pending with NDE or after rejection)
 * 
 * HELPER FUNCTIONS:
 * - renderApproverName(): Handles conditional name display logic
 * - getBadgeStyle(): Returns appropriate styling for status badges
 * - getBadgeClass(): Returns CSS class for badge background colors
 */
import { useEffect, useState } from "react";
import { getClosureRepairWorkflow } from "../../Services/weldingRepairAnalysis-service";
import { Accordion, Alert, Badge, Col, Form, Image, ListGroup, Row } from "react-bootstrap";
import Track from "../../icons/Track.svg";
import DownArrow from "../../icons/DownArrow.svg";
import { isAlphanumericWithSpecialChars } from "../../functions/validations";
import { getInitials, formatDateTimeAMPM } from "../../functions/welding-functions";

const ClosureRemarksWeldingRepair = (props) => {
  const [closureremarksobj, setClosureRemarksObj] = useState([]);
  const [collapse, setCollapse] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [error, setError] = useState(props.errorData);
  const [loopCount, setLoopCount] = useState(1);
  let levelCountForWeldingRepair = [
    { levelName: "1", approverDesignation: "Welding Engineer", approverPendingWith: "WE" },
    { levelName: "2", approverDesignation: "QC Engineer", approverPendingWith: "QCE" },
    { levelName: "3", approverDesignation: "NDE", approverPendingWith: "NDE" },
  ];

  let Role = sessionStorage.getItem("Role");

  const getAllRecords = async () => {
    let tmpObj = { fieldValue: props.rowData.reportNumber, flag: "All" };
    const Records = await getClosureRepairWorkflow(tmpObj);
    let currRevision = [];
    if (Records?.length > 0) {
      setClosureRemarksObj(Records);
      currRevision = Records.filter(
        (item) => item.revisionNumber === props.rowData.workflowRevisionNumber
      );
      setCollapse({ activeItem: props.rowData.workflowRevisionNumber });
      if (props.rowData.approvalPendingWith === "Supervisor") {
        if (currRevision[0].repairReqApprover1Status === "Rejected") {
          setLoopCount(2);
        }
        else if (currRevision[0].repairReqApprover2Status === "Rejected") {
          setLoopCount(3);
        }
        else if (currRevision[0].repairReqApprover3Status === "Rejected") {
          setLoopCount(4);
        }
      }
    }
  };

  const handleCollapsible = (collpasedItem) => {
    if (collapse.activeItem === collpasedItem) {
      setCollapse({ activeItem: -1 });
    } else {
      setCollapse({ activeItem: collpasedItem });
    }
  };

  function handleChange(e, currentClosureObj, currentWeldObj, levelnm) {
    setError((prevState) => ({
      ...prevState,
      ["repairReqApprover" + levelnm + "Status"]: { status: false, message: "" }
    }));
    props.remarksData(e, currentClosureObj, currentWeldObj, levelnm);
  }

  // Helper function to render approver name
  const renderApproverName = (ind, value) => {
    if (Role === props.rowData.approvalPendingWith || value["repairReqApprover" + ind.levelName + "Status"] === "Approved") {
      return (
        <div className="fw-bold">
          {value["repairReqApprover" + ind.levelName]}
        </div>
      );
    } else {
      return (
        <div className="fw-bold">
          Pending With
        </div>
      );
    }
  };

  // Helper function to get badge style
  const getBadgeStyle = (status) => {
    let color, background;
    if (status === "Approved") {
      color = "var(--godrej-green-alt)";
      background = "var(--godrej-green-bg)";
    } else if (status === "In-Progress" || status === "In Progress") {
      color = "var(--godrej-orange)";
      background = "var(--godrej-orange-bg)";
    } else {
      color = "var(--godrej-red)";
      background = "var(--godrej-red-bg)";
    }
    return { position: "relative", color, background };
  };

  // Helper function to get badge background class
  const getBadgeClass = (status) => {
    if (status === "Approved") return "sucessapproved";
    if (status === "In-Progress") return "inprogressapproved";
    return "rejctedapproved";
  };




  useEffect(() => {
    if (props.rowData.approvalPendingWith === "WE") {
      setLoopCount(2);
    }
    else if (props.rowData.approvalPendingWith === "QCE") {
      setLoopCount(3);
    }
    else if (props.rowData.approvalPendingWith === "NDE") {
      setLoopCount(4);
    }
    getAllRecords();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAlertMessage("");
    }, 1000);
    return () => clearTimeout(timer);
  }, [alertMessage]);

  return (
    <>
      <Accordion
        defaultActiveKey="1"
        style={{
          marginTop: "20px"
        }}
      >
        {closureremarksobj?.map((value, index) => {
          return (
            <Accordion.Item eventKey={index}
              style={{
                backgroundColor: "white",
                marginBottom: "2rem"
              }}
              key={value}
            >
              <Accordion.Header className={
                collapse?.activeItem === index
                  ? `dispaly-none accordion-header`
                  : `accordion-header`
              }
                onClick={() => handleCollapsible(index)}
              >
                <Image
                  src={Track}
                  alt="Track"
                  className="profile-initials"
                  style={{ position: "relative", left: "4.2%" }}
                ></Image>
                <Form.Label
                  className="remarkLabel size-20"
                  style={{ position: "relative", left: "60px" }}
                >
                  Track Report - Rev.{index}
                </Form.Label>
                <Image
                  src={DownArrow}
                  alt="DownArrow"
                  className="blue-arrow downarrow"
                  style={{ position: "relative", left: "40px" }}

                ></Image>
              </Accordion.Header>
              <Accordion.Body
                className={
                  collapse?.activeItem !== index ? `dispaly-none` : `show-block`
                }
              >
                <Form>
                  <ListGroup as="ol">
                    <ListGroup.Item
                      as="li"
                      className="d-flex justify-content-between align-items-start"
                      style={{ borderLeft: "none", borderTop: "none", borderRight: "none" }}
                    >
                      <div
                        className={"profile-initialsClosure "}
                        style={{ marginLeft: "-0.4%" }}>
                        {getInitials(
                          value.repairSupervisorName
                        )}
                      </div>
                      <div className="ms-5 me-auto">
                        <div className="fw-bold">
                          {value.repairSupervisorName}
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "grey",
                            paddingTop: "122px",
                          }}>
                          Supervisor
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "grey",
                          paddingRight: "32px",
                        }}
                      >
                        {formatDateTimeAMPM(value.repairSupervisorCreatedOn)}
                      </span>
                      <Badge bg="light" style={{ color: "black" }} >
                        Initiated
                      </Badge>
                    </ListGroup.Item>
                    {/* 
                      APPROVAL LEVELS SECTION
                      Maps through approval hierarchy and shows relevant approvers
                      
                      COMPLEX VISIBILITY LOGIC WITH LOOP COUNT:
                      
                      LOOP COUNT INTEGRATION:
                      The key condition (ind.levelName < loopCount) uses loopCount to:
                      - Control progressive disclosure of approval levels
                      - Hide future approvers until workflow reaches them
                      - Show appropriate context during rejections
                      
                      FULL VISIBILITY CONDITIONS:
                      1. Show approver if current revision AND level < loopCount (MAIN CONTROL)
                      2. OR show if older revision (always show completed workflow)
                      3. AND previous approvers haven't rejected (workflow integrity)
                      4. AND current approver hasn't rejected (clean display)
                      
                      LOOP COUNT EXAMPLES:
                      - loopCount=2, levelName=1 → Show WE (1 < 2 = true)
                      - loopCount=2, levelName=2 → Hide QCE (2 < 2 = false)
                      - loopCount=3, levelName=2 → Show QCE (2 < 3 = true)
                    */}
                    {levelCountForWeldingRepair.map((ind) => {
                      // ================================================================
                      // STEP-BY-STEP VISIBILITY EVALUATION - MUCH MORE READABLE
                      // ================================================================
                      
                      // STEP 1: Determine if this is the latest (current) revision
                      const isLatestRevision = index === closureremarksobj.length - 1;
                      const isOlderRevision = index < closureremarksobj.length - 1;
                      
                      // STEP 2: Check if this approval level should be visible based on workflow progress
                      const isWithinScope = ind.levelName < loopCount;
                      
                      // STEP 3: Combine revision and scope conditions
                      const shouldShow = (isLatestRevision && isWithinScope) || isOlderRevision;
                      
                      // STEP 4: Check rejection status to maintain clean workflow display
                      const prevApproverKey = "repairReqApprover" + (ind.levelName - 1) + "Status";
                      const currApproverKey = "repairReqApprover" + (ind.levelName - 2) + "Status";
                      const noPrevRejection = value[prevApproverKey] !== "Rejected";
                      const noCurrRejection = value[currApproverKey] !== "Rejected";
                      
                      // STEP 5: Early return if conditions not met
                      if (!shouldShow || !noPrevRejection || !noCurrRejection) {
                        return null;
                      }
                      
                      // STEP 6: Render the approver component
                      return (
                        <ListGroup.Item
                          key={`approver-${ind.levelName}-${index}`}
                          as="li"
                          className="d-flex justify-content-between align-items-start"
                          style={{ borderLeft: "none", borderTop: "none", borderRight: "none" }}
                        >
                          {/* Approver Avatar with Initials */}
                          <div
                            className={"profile-initialsClosure "}
                            style={{ marginLeft: "-0.4%" }}
                          >
                            {getInitials(
                              value["repairReqApprover" + ind.levelName]
                            )}
                          </div>
                          
                          {/* Approver Information and Forms Container */}
                          <div className="ms-5 me-auto" style={{ width: "20%" }}>
                            {/* 
                              CONDITIONAL NAME DISPLAY
                              Shows actual name if user is current approver OR already approved
                              Shows "Pending With" for future approvers
                            */}
                            {renderApproverName(ind, value)}
                            
                            {/* Approver Designation */}
                            <div style={{ marginBottom: "10px" }}>
                              <span style={{ fontSize: "12px", color: "grey" }}>
                                {ind.approverDesignation}
                              </span>
                            </div>
                            
                            {/* 
                              APPROVAL FORM OR COMMENT DISPLAY
                              Shows interactive form for current approver
                              Shows read-only comments for completed approvals
                            */}
                            {
                              (index === closureremarksobj.length - 1)
                                &&
                                props.rowData.approvalPendingWith === ind.approverPendingWith
                                && props.action !== "View"
                                ? (
                                    <>
                                    
                                      {Role === props.rowData.approvalPendingWith ?
                                        <Form.Group className="g-input">
                                          <div style={{ width: "400%" }}>
                                            <input
                                              id={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Comment"
                                              }
                                              name={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Comment"
                                              }
                                              type="text"
                                              className={
                                                error["repairReqApprover" +
                                                  ind.levelName +
                                                  "Comment"]
                                                  .status
                                                  ? "placeHolderInput inputText text-field-with-error"
                                                  : "placeHolderInput inputText"
                                              }
                                              placeholder=" "
                                              onChange={(e) => handleChange(e, value, props.rowData, ind.levelName)}
                                              style={{ width: "80%" }}
                                              onBlur={(e) => {
                                                let error =
                                                  isAlphanumericWithSpecialChars(
                                                    e.target.value
                                                  );
                                                setError((prevState) => ({
                                                  ...prevState,
                                                  ["repairReqApprover" + ind.levelName + "Comment"]: error,
                                                }));
                                              }}
                                            />
                                            <Form.Label htmlFor={
                                              "repairReqApprover" +
                                              ind.levelName +
                                              "Comment"
                                            }
                                            >
                                              Comments
                                            </Form.Label>
                                          </div>
                                          &nbsp;&nbsp;
                                          {error["repairReqApprover" +
                                            ind.levelName +
                                            "Comment"].status && (
                                              <Form.Text className="text-danger" style={{ fontSize: "12px", position: "relative", right: "10px" }}>
                                                {
                                                  error["repairReqApprover" +
                                                    ind.levelName +
                                                    "Comment"]
                                                    .message
                                                }
                                              </Form.Text>
                                            )}
                                        </Form.Group>
                                        : ""
                                      }
                                      {Role === props.rowData.approvalPendingWith ?
                                        <div className="m-input" style={{ position: "relative", bottom: "10px" }}>
                                          <div className={
                                            error["repairReqApprover" +
                                              ind.levelName +
                                              "Status"]
                                              .status
                                              ? "placeHolderInput inputText text-field-with-error d-flex"
                                              : "placeHolderInput inputText d-flex"
                                          }>
                                            <input
                                              id={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Status"
                                              }
                                              type="radio"
                                              name={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Status"
                                              }
                                              value="Approved"
                                              onChange={(e) => handleChange(e, value, props.rowData, ind.levelName)}
                                            />
                                            <span>&nbsp;Approve</span>
                                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                            <input
                                              id={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Status"
                                              }
                                              type="radio"
                                              name={
                                                "repairReqApprover" +
                                                ind.levelName +
                                                "Status"
                                              }
                                              value="Rejected"
                                              onChange={(e) =>
                                                handleChange(e, value, props.rowData, ind.levelName)
                                              }
                                            />
                                            <span>&nbsp;Reject</span>
                                          </div>
                                          <div className="mt-1">
                                            {error["repairReqApprover" +
                                              ind.levelName +
                                              "Status"].status && (
                                                <Form.Text className="text-danger" style={{ fontSize: "12px" }}>
                                                  {
                                                    error["repairReqApprover" +
                                                      ind.levelName +
                                                      "Status"]
                                                      .message
                                                  }
                                                </Form.Text>
                                              )}
                                          </div>
                                        </div>

                                        : ""}
                                    </>
                                  ) : (
                                    <i style={{ fontSize: "12px", color: "grey" }}>
                                      {value[
                                        "repairReqApprover" +
                                        ind.levelName +
                                        "Comment"
                                      ]
                                      }
                                    </i>
                                  )}
                            </div>
                            <div className="ms-2 me-auto"></div>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "grey",
                                paddingRight: "32px",
                              }}
                            >
                              {(() => {
                                const createdOn = value["repairReqApprover" + ind.levelName + "CreatedOn"];
                                return createdOn === "" ? "" : formatDateTimeAMPM(createdOn);
                              })()}
                            </span>
                            <Badge
                              style={getBadgeStyle(value["repairReqApprover" + ind.levelName + "Status"])}
                              bg={getBadgeClass(value["repairReqApprover" + ind.levelName + "Status"])}
                            >
                              {
                                value[
                                "repairReqApprover" +
                                ind.levelName +
                                "Status"
                                ]
                              }
                            </Badge>
                          </ListGroup.Item>
                        );
                    })}
                  </ListGroup>
                </Form>
                <Row style={{ alignContent: "right" }}>
                  <Col style={{ padding: "0" }}>
                    <div className="container history-box"></div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}

      </Accordion >

      <Row>
        {alertMessage && (
          <Alert className="alertBox" variant="warning">
            {alertMessage}
          </Alert>
        )}
      </Row>
    </>
  );
};
export default ClosureRemarksWeldingRepair;
